import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Botao } from './forma.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { usePode } from '../lib/sessao.tsx';
import './painelAgente.css';

/* O agente da sala de RX visto da recepção.
 *
 * O botão não executa nada na hora: o agente roda dentro da rede da clínica e
 * o servidor não alcança ele. O que o botão faz é deixar um recado, que o
 * agente pega no próximo contato -- por isso a tela mostra, junto, se ele está
 * respondendo. Um botão que só dissesse "pronto" mandaria a recepção embora
 * achando que o exame já entrou. */

interface Agente {
  nome: string;
  versao: string;
  ultimoContato: string | null;
  filaPendente: number;
  ultimoErro: string | null;
  varreuEm: string | null;
  novosNaUltima: number;
  online: boolean;
  pedidoPendente: boolean;
}

function haQuanto(iso: string | null): string {
  if (!iso) return 'nunca';
  const seg = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seg < 60) return `há ${seg}s`;
  if (seg < 3600) return `há ${Math.round(seg / 60)} min`;
  if (seg < 86400) return `há ${Math.round(seg / 3600)} h`;
  return `há ${Math.round(seg / 86400)} dias`;
}

/** Seta em círculo: "olhe a pasta de novo". Desenhada aqui em vez de vir de uma
 *  biblioteca de ícones -- é o único ícone do sistema, e puxar um pacote
 *  inteiro por causa dele sairia mais caro que as trinta linhas. */
function SetaCircular() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path d="M20 4v4.5h-4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PainelAgente() {
  const qc = useQueryClient();
  const pode = usePode();
  const podePedir = pode('pode_exame_cadastrar', 'pode_exame_alterar');

  const dados = useQuery({
    queryKey: ['agente'],
    queryFn: ({ signal }) => api.get<{ itens: Agente[] }>('/agente', signal),
    // Acompanha o agente enquanto a tela estiver aberta: é assim que o pedido
    // deixa de estar pendente sozinho, sem ninguém recarregar a página.
    refetchInterval: 10_000,
    retry: false,
  });

  const varrer = useMutation({
    mutationFn: () => api.post<{ aviso: string | null }>('/agente/varrer', {}),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['agente'] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
      if (r.aviso) alert(r.aviso);
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível pedir a captura.'),
  });

  const itens = dados.data?.itens ?? [];
  if (itens.length === 0) return null;

  const algumPendente = itens.some((a) => a.pedidoPendente);
  const algumOnline = itens.some((a) => a.online);

  return (
    <section className="agente">
      <div className="agente__estado">
        {itens.map((a) => (
          <div className="agente__um" key={a.nome}>
            <span className="agente__luz" data-online={a.online || undefined} />
            <span className="agente__nome">{a.nome}</span>
            <span className="agente__apoio">
              {a.online
                ? `buscou ${haQuanto(a.varreuEm)}`
                : `sem resposta ${haQuanto(a.ultimoContato)}`}
              {a.filaPendente > 0 && ` · ${a.filaPendente} na fila`}
            </span>
          </div>
        ))}
      </div>

      {podePedir && (
        <Botao
          className="agente__buscar"
          tamanho="sm"
          variante="neutro"
          carregando={varrer.isPending}
          disabled={algumPendente}
          onClick={() => varrer.mutate()}
          title={
            algumOnline
              ? 'Pede ao computador da sala de RX que olhe a pasta do eFilm agora'
              : 'O agente não está respondendo; o pedido ficará guardado'
          }
        >
          <SetaCircular />
          {algumPendente ? 'Pedido enviado…' : 'Buscar exames agora'}
        </Botao>
      )}
    </section>
  );
}
